import React, { useState, useEffect } from 'react';
import { useVoiceConversation } from '../../hooks/useVoiceConversation';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Menu, MessageSquare } from 'lucide-react';
import { useChatInterface } from '@/contexts/ChatInterfaceContext';
import { useIsMobile } from '@/hooks/use-mobile';

interface VoiceCallInterfaceProps {
  positId: string;
  onEndCall: () => void;
}

export const VoiceCallInterface: React.FC<VoiceCallInterfaceProps> = ({ 
  positId, 
  onEndCall 
}) => {
  const { 
    isConnected, 
    isConnecting,
    isAgentSpeaking, 
    isProcessing,
    canRetry,
    startVoiceConversation, 
    endVoiceConversation
  } = useVoiceConversation(positId);

  const { toggleSidebar } = useChatInterface();
  const isMobile = useIsMobile();

  // Auto-start conversation when component mounts
  useEffect(() => {
    if (positId) {
      startVoiceConversation();
    }
  }, [positId, startVoiceConversation]);

  const handleEndCall = async () => {
    endVoiceConversation();
    onEndCall();
  };

  const handleRetry = async () => {
    if (canRetry) {
      startVoiceConversation();
    }
  };

  // Show connecting state while connecting
  if (isConnecting && !isConnected) {
    return (
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={onEndCall}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-primary" />
              <h1 className="text-lg font-semibold">Voice Chat</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isMobile && (
              <Button
                variant="outline"
                size="sm"
                onClick={toggleSidebar}
                className="gap-2"
              >
                <Menu className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex items-center justify-center bg-background px-4">
          <div className="text-center mb-8">
            <div className="text-slate-600 text-lg mb-4">Connecting...</div>
            <div className="text-muted-foreground mb-6 max-w-md">
              Setting up your voice connection...
            </div>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600 mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  // Show retry option if connection failed and retries are available
  if (!isConnected && !canRetry && !isConnecting) {
    return (
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={onEndCall}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-primary" />
              <h1 className="text-lg font-semibold">Voice Chat</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isMobile && (
              <Button
                variant="outline"
                size="sm"
                onClick={toggleSidebar}
                className="gap-2"
              >
                <Menu className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex items-center justify-center bg-background px-4">
          <div className="text-center mb-8">
            <div className="text-red-600 text-lg mb-4">Connection failed</div>
            <div className="text-muted-foreground mb-6 max-w-md">
              Unable to connect to voice service. Please check your connection and try again.
            </div>
            
            <div className="flex gap-3 justify-center">
              <button
                onClick={handleRetry}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-3 px-6 rounded-lg"
              >
                Try Again
              </button>
              <button
                onClick={onEndCall}
                className="bg-slate-600 hover:bg-slate-700 text-white font-medium py-3 px-6 rounded-lg"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={onEndCall}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-primary" />
            <h1 className="text-lg font-semibold">Voice Chat</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isMobile && (
            <Button
              variant="outline"
              size="sm"
              onClick={toggleSidebar}
              className="gap-2"
            >
              <Menu className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Voice Interface Content */}
      <div className="flex-1 flex items-center justify-center bg-background px-4">
        <div className="text-center mb-8">
          {/* Modern status indicator */}
          <div className="mb-6">
            <div className={`w-24 h-24 rounded-full border-4 mx-auto mb-4 ${
              isConnected 
                ? isProcessing
                  ? 'border-slate-500 bg-slate-50 animate-pulse'
                  : isAgentSpeaking 
                    ? 'border-blue-600 bg-blue-50 animate-pulse' 
                    : 'border-emerald-500 bg-emerald-50'
                : 'border-gray-400 bg-gray-50 animate-spin'
            }`}>
              <div className={`w-4 h-4 rounded-full mx-auto mt-8 ${
                isConnected
                  ? isProcessing
                    ? 'bg-slate-500'
                    : isAgentSpeaking
                      ? 'bg-blue-600'
                      : 'bg-emerald-500'
                  : 'bg-gray-400'
              }`} />
            </div>
            
            <div className="text-xl font-medium text-foreground mb-2">
              {isConnected 
                ? isProcessing 
                  ? 'Processing...' 
                  : 'Call Active' 
                : 'Connecting...'}
            </div>
            
            <div className="text-muted-foreground">
              {isConnected 
                ? isProcessing
                  ? 'Analyzing your message with Posit knowledge...'
                  : isAgentSpeaking 
                    ? 'AI is speaking' 
                    : 'Listening...'
                : 'Establishing connection'
              }
            </div>
          </div>

          {/* End call button */}
          <button
            onClick={handleEndCall}
            className="bg-red-600 hover:bg-red-700 text-white font-medium py-4 px-8 rounded-full text-lg min-w-[120px]"
          >
            End Call
          </button>
        </div>
      </div>
    </div>
  );
}; 